import React, { useState, useMemo, useEffect } from "react";
import {
  Upload, Button, Typography, Alert, Tag, Progress, Tooltip,
  Select, Input, Space, Empty,
} from "antd";
import {
  InboxOutlined, FolderOpenOutlined, ClearOutlined, FilePdfOutlined,
  SearchOutlined, CheckCircleOutlined, DeleteOutlined, DisconnectOutlined,
} from "@ant-design/icons";
import { isPdfFile, REPORT_FIELD, METADATA_FIELD, roleOf, splitName } from "./matchFilesToRows";

const { Dragger } = Upload;
const { Text } = Typography;
const { Option } = Select;

function Counter({ label, done, total }) {
  const complete = done >= total;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, verticalAlign: "middle" }}>
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        <Progress
          type="circle"
          width={18}
          percent={total ? Math.round((done / total) * 100) : 0}
          showInfo={false}
          strokeColor={complete ? "#52C41A" : "#faad14"}
        />
      </span>
      <Text style={{ fontSize: 12, lineHeight: 1 }}>
        {done}/{total} {label}
      </Text>
    </span>
  );
}

// Generate a human-readable label for each row in the order selector
function formatRowOption(row, index) {
  const parts = [];
  if (row.sampleRegistrationNumber) parts.push(`Reg: ${row.sampleRegistrationNumber}`);
  if (row.batchId) parts.push(`Batch: ${row.batchId}`);
  if (row.commodity) parts.push(row.commodity);
  if (row.company) parts.push(row.company);
  const detail = parts.length ? ` — ${parts.join(" · ")}` : "";
  return `Row ${index + 1}${detail}`;
}

export default function BulkFileDrop({
  rows = [],
  summary,
  pdfFiles = [],
  onAttach,
  onDetachAll,
  onAssignFile,
  onDetachFile,
  onRemovePdfFile,
  onClearAllPdfFiles,
}) {
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!pdfFiles || pdfFiles.length === 0) {
      setSearchTerm("");
    }
  }, [pdfFiles]);

  const total = rows.length;
  const withReport = rows.filter((row) => row[REPORT_FIELD]).length;
  const withMetadata = rows.filter((row) => row[METADATA_FIELD]).length;
  const anyAttached = withReport > 0 || withMetadata > 0;
  const complete = total > 0 && withReport === total && withMetadata === total;

  // Filter incoming files so only non-empty PDF files are passed
  const beforeUpload = (file, fileList) => {
    if (file === fileList[fileList.length - 1]) {
      const zeroByteFiles = fileList.filter((f) => f.size === 0);
      const validPdfs = fileList.filter(isPdfFile);
      const nonPdfCount = fileList.length - validPdfs.length - zeroByteFiles.length;
      onAttach(validPdfs, nonPdfCount, zeroByteFiles.length);
    }
    return Upload.LIST_IGNORE;
  };

  // Filtered list of PDF files
  const displayedPdfFiles = useMemo(() => {
    return pdfFiles.filter((file) => {
      return !searchTerm || file.name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [pdfFiles, searchTerm]);

  const unmatched = summary ? summary.unmatched || [] : [];
  const ignoredNonPdf = summary ? summary.ignoredNonPdf || 0 : 0;
  const zeroByteCount = summary ? summary.zeroByteCount || 0 : 0;

  return (
    <div style={{ marginBottom: 16, border: "1px solid #d9d9d9", borderRadius: 6, overflow: "hidden", background: "#fff" }}>
      {/* Header Bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
        padding: "10px 14px", background: "#fafafa", borderBottom: "1px solid #f0f0f0",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <Space>
            <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
            <Text strong>Report &amp; Metadata PDF Upload</Text>
          </Space>
          <Counter label="reports" done={withReport} total={total} />
          <Counter label="metadata" done={withMetadata} total={total} />
        </div>
        {anyAttached && (
          <Tooltip title="Removes every attached file from every row — the rows themselves are untouched">
            <Button size="small" icon={<ClearOutlined />} onClick={onDetachAll}>Detach all</Button>
          </Tooltip>
        )}
      </div>

      <div style={{ padding: 14 }}>
        {/* PDF Dropzone */}
        <Dragger
          multiple
          accept=".pdf,application/pdf"
          showUploadList={false}
          beforeUpload={beforeUpload}
          style={{ padding: "14px 16px", background: complete ? "#f6ffed" : "#fafafa", borderRadius: 6 }}
        >
          <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "#262626" }}>
            <InboxOutlined style={{ marginRight: 8, color: "#0a8080", fontSize: 22 }} />
            Drop PDF files or folders here, or click to browse
          </p>
          <p className="ant-upload-hint" style={{ margin: "6px 0 10px", fontSize: 12, color: "#8c8c8c" }}>
            Upload single PDF, multiple PDFs, or an entire folder of PDF files. Only non-empty <b>.pdf</b> files are accepted.
          </p>

          <Space size="middle" onClick={(e) => e.stopPropagation()}>
            <Upload multiple accept=".pdf,application/pdf" showUploadList={false} beforeUpload={beforeUpload}>
              <Button size="small" icon={<FilePdfOutlined />} style={{ color: "#0a8080", borderColor: "#0a8080" }}>
                Select PDF Files (Single or Multiple)
              </Button>
            </Upload>
            <Upload directory multiple accept=".pdf,application/pdf" showUploadList={false} beforeUpload={beforeUpload}>
              <Button size="small" icon={<FolderOpenOutlined />} style={{ color: "#0a8080", borderColor: "#0a8080" }}>
                Select Folder
              </Button>
            </Upload>
          </Space>
        </Dragger>

        {pdfFiles.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {pdfFiles.length} valid PDF file{pdfFiles.length === 1 ? "" : "s"} in pool
            </Text>
          </div>
        )}

        {/* Alerts for zero-byte or non-PDF files / match results */}
        {zeroByteCount > 0 && (
          <Alert
            style={{ marginTop: 10 }}
            type="error"
            showIcon
            message={`${zeroByteCount} empty file${zeroByteCount === 1 ? "" : "s"} (0 bytes) rejected`}
            description="Files with zero bytes size are invalid and cannot be attached to orders."
          />
        )}

        {ignoredNonPdf > 0 && (
          <Alert
            style={{ marginTop: 10 }}
            type="info"
            showIcon
            message={`${ignoredNonPdf} non-PDF file${ignoredNonPdf === 1 ? "" : "s"} ignored`}
            description="Only PDF files (.pdf) are supported for report and metadata document attachments."
          />
        )}

        {summary && (
          <Alert
            style={{ marginTop: 10 }}
            type={unmatched.length ? "warning" : "success"}
            showIcon
            message={
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                Matched {summary.reports} report{summary.reports === 1 ? "" : "s"} and{" "}
                {summary.metadata} metadata file{summary.metadata === 1 ? "" : "s"} across{" "}
                {summary.rowsTouched} row{summary.rowsTouched === 1 ? "" : "s"}
                {unmatched.length > 0 && ` — ${unmatched.length} PDF file${unmatched.length === 1 ? "" : "s"} need manual row assignment below`}
              </span>
            }
          />
        )}

        {/* Uploaded PDF Files Pool List */}
        {pdfFiles.length > 0 && (
          <div style={{ marginTop: 16, border: "1px solid #e8e8e8", borderRadius: 6, padding: "12px 14px", background: "#fafafa" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              <div>
                <Text strong style={{ fontSize: 13 }}>
                  Uploaded PDF Files ({pdfFiles.length})
                </Text>
                <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                  Each PDF file can be assigned once to an order row's <b>Report File</b> or <b>Report Metadata File</b>:
                </Text>
              </div>

              <Space>
                <Input
                  size="small"
                  placeholder="Search PDF name..."
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                  style={{ width: 220 }}
                />

                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={onClearAllPdfFiles}
                  title="Remove all PDF files from the pool and detach all assignments"
                >
                  Clear All PDFs
                </Button>
              </Space>
            </div>

            <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 4, background: "#fff" }}>
              {displayedPdfFiles.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No PDF files match your search" style={{ padding: 12 }} />
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f5f5f5", borderBottom: "1px solid #e8e8e8", textAlign: "left" }}>
                      <th style={{ padding: "6px 12px", width: "35%" }}>PDF File Name</th>
                      <th style={{ padding: "6px 12px", width: "12%" }}>Size</th>
                      <th style={{ padding: "6px 12px", width: "38%" }}>Assigned Row Mapping (Read-only)</th>
                      <th style={{ padding: "6px 12px", width: "15%", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedPdfFiles.map((file) => {
                      const assignment = rows.reduce((found, r, idx) => {
                        if (found) return found;
                        if (r[REPORT_FIELD] && r[REPORT_FIELD].name === file.name) {
                          return {
                            row: r,
                            rowId: r.rowId,
                            rowIdx: idx + 1,
                            field: REPORT_FIELD,
                            type: "Report",
                          };
                        }
                        if (r[METADATA_FIELD] && r[METADATA_FIELD].name === file.name) {
                          return {
                            row: r,
                            rowId: r.rowId,
                            rowIdx: idx + 1,
                            field: METADATA_FIELD,
                            type: "Metadata",
                          };
                        }
                        return null;
                      }, null);

                      const rowDetails = assignment
                        ? [assignment.row.company, assignment.row.commodity, assignment.row.sampleVariant]
                            .filter(Boolean)
                            .join(" - ")
                        : null;

                      return (
                        <tr key={file.name} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          {/* File Name - NOT truncated */}
                          <td style={{ padding: "8px 12px" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                              <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: 15, flexShrink: 0 }} />
                              <span style={{ fontWeight: 500, wordBreak: "break-all", whiteSpace: "normal" }}>
                                {file.name}
                              </span>
                            </span>
                          </td>

                          {/* File Size */}
                          <td style={{ padding: "8px 12px", color: "#8c8c8c", whiteSpace: "nowrap" }}>
                            {Math.round((file.size || 0) / 1024)} KB
                          </td>

                          {/* Assigned Mapping (Read-only) */}
                          <td style={{ padding: "8px 12px" }}>
                            {assignment ? (
                              <Tag color={assignment.type === "Report" ? "blue" : "purple"} style={{ margin: 0, fontWeight: 500 }}>
                                Row {assignment.rowIdx} ({assignment.type})
                              </Tag>
                            ) : (
                              <Tag color="default" style={{ margin: 0, color: "#8c8c8c" }}>
                                Unassigned
                              </Tag>
                            )}
                          </td>

                          {/* Actions: Unmap (if mapped) + Delete file */}
                          <td style={{ padding: "8px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                            <Space size="small">
                              {assignment && (
                                <Tooltip title={`Unassign from Row ${assignment.rowIdx} (${assignment.type})`}>
                                  <Button
                                    size="small"
                                    type="link"
                                    danger
                                    icon={<DisconnectOutlined />}
                                    style={{ padding: "0 4px", fontSize: 11 }}
                                    onClick={() => onDetachFile && onDetachFile(assignment.rowId, assignment.field)}
                                  >
                                    Unmap
                                  </Button>
                                </Tooltip>
                              )}
                              <Tooltip title="Remove PDF from pool">
                                <Button
                                  size="small"
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  style={{ padding: 0 }}
                                  onClick={() => onRemovePdfFile && onRemovePdfFile(file.name)}
                                />
                              </Tooltip>
                            </Space>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
